import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Clock3 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

const WL_STYLES = {
  waiting: "bg-amber-400/10 text-amber-500 border-amber-400/30",
  notified: "bg-blue-400/10 text-blue-500 border-blue-400/30",
  booked: "bg-emerald-400/10 text-emerald-500 border-emerald-400/30",
  cancelled: "bg-red-400/10 text-red-500 border-red-400/30",
};

export default function QueueTab({ restaurant }) {
  const today = new Date().toISOString().slice(0, 10);
  const [waitlist, setWaitlist] = useState([]);
  const [wlDate, setWlDate] = useState(today);
  const [walkins, setWalkins] = useState([]);
  const [tables, setTables] = useState([]);
  const [wiForm, setWiForm] = useState({ name: "", party_size: "2", quoted_wait: "15" });

  const loadWaitlist = () =>
    api.get(`/restaurants/${restaurant.id}/waitlist`, { params: { date: wlDate } }).then((r) => setWaitlist(r.data)).catch(() => {});
  const loadWalkins = () =>
    api.get(`/restaurants/${restaurant.id}/walkins`).then((r) => setWalkins(r.data)).catch(() => {});

  useEffect(() => { loadWaitlist(); }, [wlDate, restaurant.id]);
  useEffect(() => {
    loadWalkins();
    api.get(`/restaurants/${restaurant.id}/tables`).then((r) => setTables(r.data.filter((t) => t.active))).catch(() => {});
  }, [restaurant.id]);

  const setWlStatus = async (wid, status) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/waitlist/${wid}`, { status });
      loadWaitlist();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const addWalkin = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/walkins`, {
        name: wiForm.name, party_size: Number(wiForm.party_size), quoted_wait: Number(wiForm.quoted_wait),
      });
      toast.success(`${wiForm.name} added to the queue`);
      setWiForm({ name: "", party_size: "2", quoted_wait: "15" });
      loadWalkins();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const patchWalkin = async (wid, body, msg) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/walkins/${wid}`, body);
      if (msg) toast.success(msg);
      loadWalkins();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const waitingCount = walkins.filter((w) => w.status === "waiting").length;

  return (
    <div data-testid="queue-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg">Waitlist</h3>
        <p className="text-xs text-muted-foreground mt-1">Diners join when a date is fully booked. Cancelling a booking auto-notifies the next in line.</p>
        <Input data-testid="waitlist-date" type="date" value={wlDate} onChange={(e) => setWlDate(e.target.value)}
          className="mt-4 bg-background border-border/60 max-w-[200px]" />
        <div className="mt-4 space-y-3">
          {waitlist.map((w) => (
            <div key={w.id} data-testid={`waitlist-row-${w.id}`}
              className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">{w.name} · party of {w.party_size}</p>
                <p className="text-xs text-muted-foreground">{w.email}{w.time ? ` · prefers ${w.time}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={WL_STYLES[w.status]}>{w.status}</Badge>
                {w.status !== "booked" && w.status !== "cancelled" && (
                  <Select value="" onValueChange={(v) => setWlStatus(w.id, v)}>
                    <SelectTrigger data-testid={`waitlist-status-${w.id}`} className="w-28 h-8 bg-background border-border/60 text-xs">
                      <SelectValue placeholder="Update" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="booked">Booked</SelectItem>
                      <SelectItem value="cancelled">Remove</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          ))}
          {waitlist.length === 0 && <p className="text-sm text-muted-foreground py-4" data-testid="no-waitlist-msg">No waitlist entries for this date.</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
          Walk-in queue
          {waitingCount > 0 && <Badge className="bg-primary text-primary-foreground" data-testid="walkin-count">{waitingCount} waiting</Badge>}
        </h3>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Input data-testid="walkin-name-input" placeholder="Name" value={wiForm.name}
            onChange={(e) => setWiForm({ ...wiForm, name: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="walkin-party-input" type="number" min="1" value={wiForm.party_size}
            onChange={(e) => setWiForm({ ...wiForm, party_size: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="walkin-wait-input" type="number" min="0" step="5" value={wiForm.quoted_wait}
            onChange={(e) => setWiForm({ ...wiForm, quoted_wait: e.target.value })} className="bg-background border-border/60"
            title="Quoted wait (minutes)" />
        </div>
        <Button data-testid="walkin-add-btn" onClick={addWalkin} disabled={!wiForm.name} className="mt-3 rounded-full" size="sm">
          <UserPlus className="w-4 h-4 mr-2" /> Add walk-in
        </Button>
        <div className="mt-5 space-y-3">
          {walkins.map((w) => (
            <div key={w.id} data-testid={`walkin-row-${w.id}`}
              className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">{w.name} · party of {w.party_size}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock3 className="w-3 h-3" /> quoted {w.quoted_wait} min
                </p>
              </div>
              <div className="flex items-center gap-2">
                {w.status === "waiting" && (
                  <>
                    <Select value="" onValueChange={(v) => patchWalkin(w.id, { status: "seated", table_id: v }, `${w.name} seated`)}>
                      <SelectTrigger data-testid={`walkin-seat-${w.id}`} className="w-28 h-8 bg-background border-border/60 text-xs">
                        <SelectValue placeholder="Seat at..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tables.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.label} ({t.capacity})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button data-testid={`walkin-left-${w.id}`} size="sm" variant="ghost" className="h-8 text-muted-foreground"
                      onClick={() => patchWalkin(w.id, { status: "left" })}>Left</Button>
                  </>
                )}
                {w.status !== "waiting" && (
                  <Badge variant="outline" className={w.status === "seated" ? "text-emerald-500 border-emerald-500/30" : "text-muted-foreground"}>
                    {w.status}
                  </Badge>
                )}
              </div>
            </div>
          ))}
          {walkins.length === 0 && <p className="text-sm text-muted-foreground py-4" data-testid="no-walkins-msg">No walk-ins today.</p>}
        </div>
      </div>
    </div>
  );
}
